import axios from 'axios';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import {
  AxiosResponseWithRefreshToken,
  ContentTypeEnum,
} from 'src/models/GalenData/Request.model';

import { captureAxiosError } from 'src/utils/errorReporting';

const baseURL = import.meta.env.VITE_BASE_URL_GALEN_DATA;

const httpRequest = axios.create({
  baseURL,
  timeout: 10_000,
  headers: {
    'X-APP-TYPE': import.meta.env.VITE_X_APP_TYPE,
    'X-API-VERSION': import.meta.env.VITE_X_API_VERSION,
    'X-TENANT-DOMAIN': import.meta.env.VITE_X_TENANT_DOMAIN,
    'Content-Type': ContentTypeEnum.JSON,
  },
});

httpRequest.interceptors.request.use((config) => {
  const authToken = useRemoteBoundStore.getState().authInfo.authToken;

  if (authToken) {
    config.headers['Authorization'] = authToken;
  }

  return config;
});

httpRequest.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    captureAxiosError(error, {
      customMessage: 'HTTP Request Error',
      operation: 'HTTP Request',
      tags: {
        error_source: 'http_request_interceptor',
      },
    });

    // 1. network error
    if (!error.response) {
      throw error;
    }

    const { response, config } = error;

    // 2. server error
    if (response?.status !== 401) {
      throw error;
    }

    const {
      authInfo: { authToken, refreshToken },
    } = useRemoteBoundStore.getState();

    // 3. refresh_token has been deleted
    if (!refreshToken) {
      // TODO: need to add AuthRouter
      // history.push('/login', { state: { from: history.location } });
      useRemoteBoundStore.getState().logOut();
      throw error;
    }

    // 4. if the auth_token has been expired, get new authorization token
    try {
      const res = await axios<void, AxiosResponseWithRefreshToken>({
        method: 'post',
        url: baseURL + '/auth/refresh-authtoken',
        headers: {
          'X-APP-TYPE': import.meta.env.VITE_X_APP_TYPE,
          'X-API-VERSION': import.meta.env.VITE_X_API_VERSION,
          'X-TENANT-DOMAIN': import.meta.env.VITE_X_TENANT_DOMAIN,
          'Content-Type': ContentTypeEnum.TEXT,
          Authorization: authToken,
        },
        data: refreshToken,
      });

      const newAuthToken = res.headers.authorization;
      const newRefreshToken = res.headers['x-refresh-token'];

      const { setAuth } = useRemoteBoundStore.getState();
      setAuth({
        authInfo: {
          authToken: newAuthToken,
          refreshToken: newRefreshToken,
        },
      });

      // Retry the original request with the new token
      config.headers['Authorization'] = newAuthToken;
      return httpRequest(config);
    } catch (refreshError) {
      // 5. if get new token failed - capture this error too
      captureAxiosError(refreshError, {
        customMessage: 'Token Refresh Failed',
        operation: 'Token Refresh',
        tags: {
          error_source: 'http_request_interceptor',
          error_type: 'token_refresh_failed',
        },
      });
      useRemoteBoundStore.getState().logOut();
      // history.push('/login', { from: history.location });
      // Toast.info('Token expired', 1);
      throw refreshError;
    }
  },
);

export default httpRequest;
